import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PlaylistPage.css";
import apiClient, { API_BASE_URL } from "../api/client";
import Header from "../components/Header.js";
import { scrollHorizontallyOnWheel } from "../utils/scroll";
import { useDragScroll } from "../hooks/useDragScroll";

// 單一音檔大小上限。伺服器 Nginx 設定 client_max_body_size 200M，
// multipart 表單本身還會多一點額外資料，所以前端抓 190MB 留緩衝，
// 超過的檔案在選擇時就擋下，不讓它送到伺服器才被 413 拒絕。
const MAX_UPLOAD_SIZE_MB = 190;
const MAX_UPLOAD_SIZE = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

const formatFileSize = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

function PlaylistPage({ isLoggedIn, onLogout }) {
  const tabsDragScroll = useDragScroll();
  const [songs, setSongs] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [renameDialog, setRenameDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [newTitleInput, setNewTitleInput] = useState("");
  // const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [playlistGroups, setPlaylistGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [newGroupInput, setNewGroupInput] = useState([]);
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [newGroupId, setNewGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentlyPlayingSong, setCurrentlyPlayingSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [editingGroupOriginalId, setEditingGroupOriginalId] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  useEffect(() => {
    fetchPlaylistGroupsFromPlaylistTable();
  }, []);

  const fetchPlaylistGroupsFromPlaylistTable = async () => {
    try {
      const res = await apiClient.get(`/api/playlists`);
      const allOption = { id: "all", name: "所有歌曲" };
      const playlists = res.data.map((p) => ({
        id: p.id,      // ✅ 這是 playlist_id，例如 PL_001
        name: p.name,  // 顯示名稱
        group: p.group // 想顯示/備用的話可留著
      }));
      setPlaylistGroups([allOption, ...playlists]);
    } catch (err) {
      console.error("取得播放清單失敗", err);
      alert("無法取得群組列表");
    }
  };

  // 將平均分資料合併到歌曲陣列（不影響你既有 API）
  const mergeAvgRatings = async (baseSongs, setSongsCb) => {
    try {
      const res = await apiClient.get(`/api/songs/avg_ratings`);
      const dict = res.data || {};
      const merged = baseSongs.map((s) => ({
        ...s,
        avg_rating: dict[s.song_id]?.avg_rating ?? null,
        rating_count: dict[s.song_id]?.rating_count ?? 0,
      }));
      setSongsCb(merged);
    } catch (err) {
      console.error("取得平均評分失敗：", err);
      // 取不到平均分也不擋畫面；維持原本 songs
      setSongsCb(baseSongs);
    }
  };

  const fetchSongs = async () => {
    try {
      const url =
        selectedGroup === "all"
          ? `/api/songs`
          : `/api/songs/${selectedGroup}`;
      const res = await apiClient.get(url);
      console.log("🎵 從後端取得的歌曲資料：", res.data);

      // 先設定，再合併平均分
      await mergeAvgRatings(res.data, setSongs);
    } catch (err) {
      console.error("無法取得歌曲清單", err);
    }
  };

  const handleAddPlaylist = () => {
    setShowAddDialog(true);
  };

  const handleFileChange = async (e) => {
    const allFiles = Array.from(e.target.files);
    // 清空 input，讓使用者被擋下後可以重新選同一個（或其他）檔案
    e.target.value = "";

    const tooLarge = allFiles.filter((file) => file.size > MAX_UPLOAD_SIZE);
    const files = allFiles.filter((file) => file.size <= MAX_UPLOAD_SIZE);

    if (tooLarge.length > 0) {
      const list = tooLarge
        .map((file) => `・${file.name}（${formatFileSize(file.size)}）`)
        .join("\n");
      alert(
        `以下檔案超過 ${MAX_UPLOAD_SIZE_MB} MB，無法上傳：\n${list}\n\n` +
          "建議先轉成較低位元率的 MP3 再上傳。"
      );
    }

    if (files.length === 0) return;

    const processedFiles = await Promise.all(
      files.map((file, idx) => {
        return new Promise((resolve) => {
          const rawName = file.name.replace(/\.[^/.]+$/, "");
          const objectUrl = URL.createObjectURL(file);
          const audio = new Audio(objectUrl);
          // 檔案本身沒有天生穩定的 id（上傳前還沒有 song_id），拖曳排序時如果
          // key 用 index，重新排序後 React 會把舊 DOM 節點誤配到新的檔案資料上，
          // 這裡自己產生一個穩定 id 給 key 用。
          const id = `${Date.now()}-${idx}-${Math.random().toString(36).slice(2)}`;

          const finish = (duration) => {
            URL.revokeObjectURL(objectUrl); // 讀完就釋放，不然開多次「新增歌曲」會一直累積記憶體
            resolve({ id, file, title: rawName, duration, progress: 0, groups: [] });
          };

          audio.addEventListener("loadedmetadata", () => finish(Math.floor(audio.duration)));
          // 檔案損毀/格式不支援時 loadedmetadata 永遠不會觸發，沒有這個
          // fallback 的話整個新增歌曲流程會卡住不動。
          audio.addEventListener("error", () => finish(0));
        });
      })
    );

    setSelectedFiles((prev) => [...prev, ...processedFiles]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDraggingIndex(index);
  };

  const handleDragOver = (index) => {
    if (draggingIndex === null || draggingIndex === index) return;
    const updatedFiles = [...selectedFiles];
    const draggedItem = updatedFiles[draggingIndex];
    updatedFiles.splice(draggingIndex, 1);
    updatedFiles.splice(index, 0, draggedItem);
    setDraggingIndex(index);
    setSelectedFiles(updatedFiles);
  };

  const handleAddSubmit = async () => {
    if (selectedFiles.length === 0) {
      alert("請選擇至少一個音檔");
      return;
    }

    if (selectedFiles.some((f) => !f.groups || f.groups.length === 0)) {
      alert("請為每首歌選擇至少一個群組");
      return;
    }

    // 送出前再確認一次大小：一旦開始上傳，會先建立 song_id 才傳檔案，
    // 如果檔案被伺服器拒絕，就會留下一筆沒有音檔的歌曲。
    const oversized = selectedFiles.filter((f) => f.file.size > MAX_UPLOAD_SIZE);
    if (oversized.length > 0) {
      alert(
        `「${oversized.map((f) => f.title).join("、")}」超過 ${MAX_UPLOAD_SIZE_MB} MB，無法上傳，請先移除。`
      );
      return;
    }

    setIsUploading(true);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const { file, title, duration, groups } = selectedFiles[i];

        // ✅ 第一步：建立單一 song_id（不指定群組）
        const createRes = await apiClient.post(
          `/api/songs/create`,
          { title }
        );
        const song_id = createRes.data.song_id;

        // ✅ 第二步：建立與群組的對應關係
        for (const group of groups) {
          await apiClient.post(`/api/songs/assignToGroup`, {
            song_id,
            group,
          });
        }

        // ✅ 第三步：上傳一次音檔，給這個 song_id
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", title);
        formData.append("duration", duration);
        formData.append("song_id", song_id);

        await apiClient.post(`/api/songs/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setSelectedFiles((prev) => {
              const updated = [...prev];
              updated[i] = { ...updated[i], progress: percentCompleted };
              return updated;
            });
          },
        });
      }

      alert("所有歌曲上傳成功！");
      await fetchSongs();
      resetUploadForm();
    } catch (err) {
      console.error("上傳失敗", err);
      if (err.response?.status === 413) {
        alert(`檔案太大，伺服器拒絕上傳（上限 ${MAX_UPLOAD_SIZE_MB} MB）`);
      } else {
        alert("上傳失敗，請稍後再試");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddGroup = async () => {
    if (!newGroupName) {
      alert("請輸入顯示名稱");
      return;
    }

    // ✅ 先檢查前端已有的群組列表
    const isDuplicate = playlistGroups.some(
      (g) => g.name.trim() === newGroupName.trim()
    );

    if (isDuplicate) {
      alert("已有相同顯示名稱的群組，請重新輸入");
      return;
    }

    try {
      await apiClient.post(`/api/addGroup`, {
        group_name: newGroupName, // 只送顯示名稱
      });

      alert("群組新增成功");
      setShowAddGroupDialog(false);
      setNewGroupName("");
      fetchPlaylistGroupsFromPlaylistTable();
    } catch (err) {
      console.error("新增群組失敗", err);
      alert("新增群組失敗，請稍後再試");
    }
  };

  const resetUploadForm = () => {
    setShowAddDialog(false);
    setSelectedFiles([]); // 清空選擇的檔案
    setIsUploading(false);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const confirmRename = async () => {
    try {
      await apiClient.post(`/api/songs/rename`, {
        song_id: selectedSong.song_id,
        old_path: selectedSong.audio_path,
        new_title: newTitleInput,
        new_group: newGroupInput,
      });

      alert("修改成功");
      setRenameDialog(false);
      setActionDialog(false);
      fetchSongs();
    } catch (err) {
      console.error("❌ 修改失敗:", err);
      if (err.response) {
        console.error("📡 Response data:", err.response.data);
        console.error("📄 Status:", err.response.status);
        console.error("📬 Headers:", err.response.headers);
        alert(
          `修改失敗：${err.response.status} - ${JSON.stringify(
            err.response.data
          )}`
        );
      } else if (err.request) {
        console.error("📭 Request made but no response:", err.request);
        alert("修改失敗：無回應，可能是 CORS 或 server 沒開");
      } else {
        console.error("⚠️ Error setting up request:", err.message);
        alert(`修改失敗：${err.message}`);
      }
    }
  };

  const confirmDelete = async () => {
    try {
      await apiClient.post(`/api/songs/delete`, {
        song_id: selectedSong.song_id,
        group_id: selectedGroup,
      });
      alert("刪除成功");
      setDeleteConfirmDialog(false);
      setActionDialog(false);
      fetchSongs();
    } catch (err) {
      console.error("刪除失敗", err);
      alert("刪除失敗");
    }
  };

  const handleConfirmLogout = () => {
    onLogout();
    navigate("/");
  };

  const filteredSongs =
    selectedGroup === "all"
      ? songs
      : songs.filter((song) => song.group_id === selectedGroup);

  const togglePlay = (song) => {
    let audio = null;

    const handleTimeUpdate = () => {
      if (audio) setProgress(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentAudio(null);
      setCurrentlyPlayingSong(null);
    };

    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio.removeEventListener("timeupdate", handleTimeUpdate);
      currentAudio.removeEventListener("ended", handleEnded);
    }

    if (currentlyPlayingSong?.song_id === song.song_id && isPlaying) {
      setIsPlaying(false);
      setCurrentAudio(null);
      setCurrentlyPlayingSong(null);
      setProgress(0);
      return;
    }

    audio = new Audio(`${API_BASE_URL}/${song.audio_path}`);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    audio
      .play()
      .then(() => {
        setCurrentAudio(audio);
        setCurrentlyPlayingSong(song);
        setIsPlaying(true);
      })
      .catch((err) => {
        console.error("音訊播放錯誤：", err);
      });
  };

  useEffect(() => {
    if (renameDialog && selectedSong?.song_id) {
      apiClient
        .post(`/api/songs/getGroupsBySong`, {
          song_id: selectedSong.song_id,
        })
        .then((res) => {
          console.log("取得所選歌曲：", selectedSong.song_id);
          console.log("取得歌曲群組資料：", res);

          const groups = res.data.groups;
          if (Array.isArray(groups)) {
            console.log("✅ 有效群組陣列：", groups);
            setNewGroupInput(groups);
          } else {
            console.warn("⚠️ 回傳的 groups 不是陣列：", groups);
            setNewGroupInput([]);
          }
        })
        .catch((err) => {
          console.error("❌ 取得歌曲組別失敗", err);
          setNewGroupInput([]);
        });
    }
  }, [renameDialog, selectedSong]);

  const handleEditGroup = async () => {
    if (!newGroupName) {
      alert("請填寫新的顯示名稱");
      return;
    }

    const payload = {
      group_id: editingGroupOriginalId,
      new_name: newGroupName,
    };

    console.log("🚀 傳送至後端的資料:", payload);

    try {
      await apiClient.post(`/api/playlist/editGroup`, {
        group_id: editingGroupOriginalId,
        new_name: newGroupName,
      });

      alert("群組修改成功");
      setShowEditGroupDialog(false);
      setNewGroupId("");
      console.log("✅ 群組編輯完成:", newGroupId);
      setNewGroupName("");
      fetchPlaylistGroupsFromPlaylistTable();
    } catch (err) {
      console.error("編輯群組失敗", err);
      alert("編輯群組失敗，請稍後再試");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    const group = playlistGroups.find((g) => g.id === groupId);
    const groupName = group?.name || groupId;

    try {
      const checkRes = await apiClient.get(
        `/api/groups/${groupId}/users`
      );

      if (Array.isArray(checkRes.data) && checkRes.data.length > 0) {
        alert(`群組「${groupName}」目前已被 ${checkRes.data} 使用者使用，無法刪除`);
        return;
      }
    } catch (err) {
      console.error("檢查群組使用狀態失敗", err);
      alert("檢查群組使用狀態失敗，請稍後再試");
      return;
    }

    const confirm = window.confirm(
      `確定要刪除群組「${groupName}」嗎？此操作會移除所有對應歌曲的關聯。`
    );
    if (!confirm) return;

    try {
      await apiClient.post(`/api/deleteGroup`, {
        group_id: groupId,
      });

      alert("群組刪除成功");
      setPlaylistGroups((prev) => prev.filter((g) => g.id !== groupId));
      setShowEditGroupDialog(false);
      setNewGroupId("");
      setNewGroupName("");
      setEditingGroupOriginalId("");
    } catch (err) {
      console.error("刪除群組失敗", err);
      alert("刪除群組失敗，請稍後再試");
    }
  };

  return (
    <>
      <Header onLogout={handleConfirmLogout} />
      <div className="playlist-page">
        <div className="playlist-container">
          <div className="playlist-header">
            <h2>🎵 歌曲清單</h2>
            <div className="add-button-container">
              <button className="add-button" onClick={handleAddPlaylist}>
                ➕ 新增歌曲
              </button>
              <button
                className="add-button"
                onClick={() => setShowAddGroupDialog(true)}
              >
                ➕ 新增組別
              </button>
              <button
                className="add-button"
                onClick={() => {
                  const editable = playlistGroups.find((g) => g.id !== "all");
                  if (!editable) return alert("無可編輯的群組");
                  setNewGroupId(editable.id);
                  setNewGroupName(editable.name);
                  setEditingGroupOriginalId(editable.id);
                  setShowEditGroupDialog(true);
                }}
              >
                ✏️ 編輯組別
              </button>
            </div>
          </div>
          <div
            className="playlist-tabs"
            onWheel={scrollHorizontallyOnWheel}
            {...tabsDragScroll}
          >
            {playlistGroups.map((group) => (
              <button
                key={group.id}
                className={`tab-button ${selectedGroup === group.id ? "active" : ""
                  }`}
                onClick={() => {
                  setSelectedGroup(group.id);
                }}
              >
                {group.name}
              </button>
            ))}
          </div>
          <div className="table-scroll">
            <table className="song-table">
              <thead>
                <tr>
                  <th>Song ID</th>
                  <th>Title</th>
                  <th>Path</th>
                  <th>Duration (秒)</th>
                  <th>Average</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredSongs.map((song) => (
                  <tr
                    key={song.song_id} // ✅ 新增 key prop
                    onDoubleClick={() => {
                      setSelectedSong(song);
                      setActionDialog(true);
                    }}
                  >
                    <td>{song.song_id}</td>
                    <td>{song.title}</td>
                    <td>{song.audio_path}</td>
                    <td>{song.duration}</td>
                    <td>
                      {song && song.rating_count > 0 && song.avg_rating !== null
                        ? Number(song.avg_rating).toFixed(1)
                        : "—"}
                    </td>
                    <td>
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                        onClick={() => togglePlay(song)}
                      >
                        ▶️
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="playlist-footer">⚠️ 請雙擊欲刪改的歌曲以進行操作</p>
          <p className="playlist-footer">
            ⚠️ 若目前選擇的是「所有歌曲」，刪除將會從所有群組中移除該歌曲。
          </p>
          <p className="playlist-footer">
            ⚠️
            若選擇的是特定群組，刪除將只會從該群組中移除歌曲，不會影響其他群組。
          </p>
        </div>
      </div>
      {currentlyPlayingSong && (
        <div className="audio-player-bar">
          <div className="player-header">
            <div className="info">
              🎧 <strong>{currentlyPlayingSong.title}</strong> —{" "}
              {Math.floor(progress)} / {currentlyPlayingSong.duration} 秒
            </div>
            <button
              className="close-player-btn"
              onClick={() => {
                if (currentAudio) {
                  currentAudio.pause();
                  currentAudio.currentTime = 0;
                }
                setIsPlaying(false);
                setCurrentAudio(null);
                setCurrentlyPlayingSong(null);
                setProgress(0);
              }}
            >
              ❌
            </button>
          </div>
          <input
            type="range"
            min="0"
            max={currentlyPlayingSong.duration}
            value={progress}
            onChange={(e) => {
              const val = Number(e.target.value);
              setProgress(val);
              if (currentAudio) currentAudio.currentTime = val;
            }}
          />
          <div className="controls">
            <button
              onClick={() => {
                if (!currentAudio) return;
                if (isPlaying) {
                  currentAudio.pause();
                } else {
                  currentAudio.play();
                }
                setIsPlaying(!isPlaying);
              }}
            >
              {isPlaying ? (
                <img src="/pause_icon.png" alt="Pause" className="audio-icon" />
              ) : (
                <img src="/play_icon.png" alt="Play" className="audio-icon" />
              )}
            </button>
          </div>
        </div>
      )}
      {showAddDialog && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <h3>新增歌曲</h3>
            {/* 原生 input 在 handleFileChange 裡會被清空（才能重選同一個檔案），
                它自己顯示的檔名會變成「未選擇任何檔案」，而且清單是累加的，
                所以藏起原生 input，改用自訂按鈕＋已選數量。 */}
            <div className="file-picker">
              <label className="file-picker-button">
                選擇檔案
                <input
                  type="file"
                  accept=".mp3,audio/mpeg"
                  multiple
                  onChange={handleFileChange}
                />
              </label>
              <span className="file-picker-status">
                {selectedFiles.length > 0
                  ? `已選擇 ${selectedFiles.length} 個檔案`
                  : "尚未選擇檔案"}
              </span>
            </div>
            <p className="upload-size-hint">
              單一檔案上限 {MAX_UPLOAD_SIZE_MB} MB
            </p>
            <div className="file-preview">
              {selectedFiles.map((f, i) => (
                <div
                  key={f.id}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragOver={() => handleDragOver(i)}
                  className="file-item"
                >
                  <span
                    className="remove-btn"
                    onClick={() => handleRemoveFile(i)}
                  >
                    ✕
                  </span>
                  <div className="file-info">
                    <strong>{f.title}</strong> - {f.duration} 秒（
                    {formatFileSize(f.file.size)}）
                    <br />
                    <label>
                      組別：
                      <div className="checkbox-group">
                        {playlistGroups
                          .filter((g) => g.id !== "all")
                          .map((group) => (
                            <label
                              key={group.id}
                              style={{
                                display: "block",
                                marginBottom: "4px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={f.groups?.includes(group.id) || false}
                                onChange={(e) => {
                                  const updated = [...selectedFiles];
                                  const targetFile = { ...updated[i] };
                                  let newGroups = targetFile.groups || [];

                                  if (e.target.checked) {
                                    newGroups = [...newGroups, group.id];
                                  } else {
                                    newGroups = newGroups.filter(
                                      (id) => id !== group.id
                                    );
                                  }

                                  targetFile.groups = newGroups;
                                  updated[i] = targetFile;
                                  setSelectedFiles(updated);
                                }}
                              />
                              {group.name}
                            </label>
                          ))}
                      </div>
                    </label>
                  </div>
                  {isUploading && (
                    <>
                      <br />
                      <progress
                        value={f.progress}
                        max="100"
                        className="progress-bar"
                      />
                      <span>{f.progress}%</span>
                    </>
                  )}
                </div>
              ))}
              <p className="playlist-footer">拖曳以更改歌曲順序</p>
            </div>
            <div className="dialog-actions">
              <button
                onClick={handleAddSubmit}
                disabled={
                  isUploading ||
                  selectedFiles.some((f) => !f.groups || f.groups.length === 0)
                }
                className="submit-button"
              >
                送出
              </button>
              <button
                onClick={resetUploadForm}
                disabled={isUploading}
                className="cancel-button"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditGroupDialog && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <h3>編輯群組</h3>
            <div className="edit-group-tabs">
              {playlistGroups
                .filter((g) => g.id !== "all")
                .map((group) => (
                  <div key={group.id} className="edit-group-tab-item">
                    <button
                      className={`tab-button ${editingGroupOriginalId === group.id ? "active" : ""
                        }`}
                      onClick={() => {
                        setEditingGroupOriginalId(group.id);
                        setNewGroupId(group.id);
                        setNewGroupName(group.name);
                      }}
                    >
                      {group.name}
                    </button>
                  </div>
                ))}
            </div>
            <label>
              群組名稱：
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </label>
            <div className="dialog-actions">
              <button
                className="danger-button"
                onClick={() => {
                  if (!editingGroupOriginalId)
                    return alert("請先選擇要刪除的群組");
                  const confirmDelete =
                    window.confirm("確定要刪除這個群組嗎？");
                  if (confirmDelete) handleDeleteGroup(editingGroupOriginalId);
                }}
              >
                刪除
              </button>
              <button onClick={handleEditGroup}>儲存</button>
              <button
                onClick={() => {
                  setShowEditGroupDialog(false);
                  setNewGroupId("");
                  setNewGroupName("");
                }}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {actionDialog && selectedSong && (
        <div className="song-action-overlay">
          <div className="song-action-modal">
            <h3 className="song-action-title">選擇操作</h3>
            <p>
              <strong>Song ID:</strong> {selectedSong.song_id}
            </p>
            <p>
              <strong>Title:</strong> {selectedSong.title}
            </p>
            <p>
              <strong>Path:</strong> {selectedSong.audio_path}
            </p>
            <p>
              <strong>Duration:</strong> {selectedSong.duration} 秒
            </p>
            <div className="song-action-buttons">
              <button
                className="song-action-btn rename"
                onClick={() => {
                  setNewTitleInput(selectedSong.title);
                  setRenameDialog(true);
                }}
              >
                修改
              </button>
              <button
                className="song-action-btn delete"
                onClick={() => setDeleteConfirmDialog(true)}
              >
                刪除
              </button>
              <button
                className="song-action-btn cancel"
                onClick={() => setActionDialog(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {renameDialog && (
        <div className="song-action-overlay">
          <div className="song-action-modal">
            <h3 className="song-action-title">修改歌曲名稱或群組</h3>
            <input
              type="text"
              value={newTitleInput}
              onChange={(e) => setNewTitleInput(e.target.value)}
              className="song-action-input"
            />
            <label>
              組別：
              <div className="checkbox-group">
                {playlistGroups
                  .filter((g) => g.id !== "all")
                  .map((group) => (
                    <label
                      key={group.id}
                      style={{ display: "block", marginBottom: "4px" }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(newGroupInput) &&
                          newGroupInput.includes(group.id)
                        }
                        onChange={(e) => {
                          let updated = [...newGroupInput];
                          if (e.target.checked) {
                            updated.push(group.id);
                          } else {
                            updated = updated.filter((id) => id !== group.id);
                          }
                          setNewGroupInput(updated);
                        }}
                      />
                      {group.name}
                    </label>
                  ))}
              </div>
            </label>
            <div className="song-action-buttons">
              <button
                className="song-action-btn rename"
                onClick={confirmRename}
              >
                確定
              </button>
              <button
                className="song-action-btn cancel"
                onClick={() => setRenameDialog(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirmDialog && (
        <div className="song-action-overlay">
          <div className="song-action-modal">
            <h3 className="song-action-title">
              確定要刪除「{selectedSong?.title}」嗎？
            </h3>
            <div className="song-action-buttons">
              <button
                className="song-action-btn delete"
                onClick={confirmDelete}
              >
                確定
              </button>
              <button
                className="song-action-btn cancel"
                onClick={() => setDeleteConfirmDialog(false)}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddGroupDialog && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <h3>新增群組</h3>
            <label>
              請輸入群組名稱：
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </label>
            <div className="dialog-actions">
              <button onClick={handleAddGroup}>送出</button>
              <button onClick={() => setShowAddGroupDialog(false)}>取消</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PlaylistPage;
